import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { formatBRLc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Gift, Check, QrCode, Rocket, Printer, ArrowRight, ShieldCheck, Infinity as InfinityIcon, HelpCircle } from "lucide-react";

const DESTAQUE = [
  { label: "3 dias", price: 15, every: "a cada 3 dias", tag: "" },
  { label: "7 dias", price: 20, every: "por semana", tag: "Mais popular" },
  { label: "15 dias", price: 35, every: "a cada 15 dias", tag: "Melhor valor" },
];

const FAQ = [
  { q: "Anunciar custa mesmo nada?", a: "Sim, 100% grátis e para sempre. Você publica quantos carros quiser, com fotos ilimitadas, sem taxa de cadastro e sem prazo de expiração do anúncio." },
  { q: "O que exatamente é pago?", a: "Apenas dois itens opcionais: (1) o Kit de Divulgação com QR — assinatura trimestral de R$ 29,00 (a cada 3 meses) que libera o QR Code exclusivo e os 3 materiais (Cartaz A4, Cartão Postal e Cartão de Visita); e (2) o destaque no topo da vitrine, também assinatura. Você só paga se quiser — o anúncio em si nunca é cobrado." },
  { q: "Como funciona a cobrança do destaque?", a: "É uma assinatura recorrente conforme o plano escolhido: R$ 15,00 a cada 3 dias, R$ 20,00 por semana ou R$ 35,00 a cada 15 dias. A renovação é automática pelo Stripe até você cancelar." },
  { q: "Posso cancelar o destaque quando quiser?", a: "Pode, sem multa. No painel, use 'Gerenciar assinatura' ou o botão 'Cancelar' no anúncio. Seu carro continua no topo até o fim do período já pago e não há nova cobrança." },
  { q: "Por quanto tempo o QR Code vale?", a: "O QR Code funciona enquanto a assinatura trimestral do Kit de Divulgação (R$ 29,00 a cada 3 meses) estiver ativa. Se você não renovar, o QR impresso simplesmente para de funcionar (quem escanear vê um aviso), mas o seu anúncio continua no ar normalmente no site. Você não precisa reimprimir nada: ao reativar a assinatura, o mesmo QR volta a funcionar." },
  { q: "Vocês cobram comissão pela venda?", a: "Nunca. Diferente de outros portais, o PicCar não tira nenhum percentual da venda. O valor negociado do carro é 100% seu." },
  { q: "Como eu pago?", a: "Todo pagamento é feito por cartão, em um checkout seguro do Stripe. Não guardamos os dados do seu cartão." },
];

export default function Precos() {
  const navigate = useNavigate();
  return (
    <div data-testid="precos-page">
      {/* FREE HERO */}
      <section className="relative overflow-hidden bg-obsidian text-white">
        <div className="absolute inset-0 grain opacity-100" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-20 md:py-28 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 bg-blaze/15 text-blaze border border-blaze/30 rounded-full px-4 py-1.5 mb-8">
            <Gift className="w-4 h-4" /> <span className="font-700 text-sm uppercase tracking-wide">Transparência total</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Todo anúncio é <span className="text-blaze">100% grátis.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
            Anunciar seu carro no PicCar não custa nada — nunca. Você só paga se quiser dois opcionais: o <b className="text-white">Kit de Divulgação com QR</b> e o <b className="text-white">destaque no topo</b>. Sem taxa escondida, sem comissão sobre a venda.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="mt-8">
            <Button className="h-13 px-8 bg-blaze hover:bg-white hover:text-obsidian text-white font-700 rounded-sm" onClick={() => navigate("/anunciar")} data-testid="precos-anunciar-btn">
              Anunciar grátis agora <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* PRICING TABLE */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
          {/* FREE */}
          <div className="bg-white border-2 border-obsidian rounded-sm p-8 relative" data-testid="plan-anuncio">
            <span className="absolute -top-3 left-8 bg-obsidian text-white text-[11px] font-800 uppercase px-3 py-1 rounded-sm">Sempre grátis</span>
            <div className="w-12 h-12 rounded-sm bg-obsidian flex items-center justify-center"><Gift className="w-6 h-6 text-blaze" /></div>
            <h3 className="mt-5 font-heading font-800 text-2xl">Anúncio</h3>
            <p className="mt-2 font-heading font-900 text-5xl text-obsidian">R$ 0</p>
            <p className="text-sm text-muted-foreground">para sempre</p>
            <ul className="mt-6 space-y-3 text-sm">
              {["Fotos ilimitadas", "Aparece na vitrine e na busca", "Chat direto e seguro no PicCar", "Sem comissão sobre a venda", "Sem prazo de expiração"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button className="mt-8 w-full bg-obsidian hover:bg-blaze text-white font-700 rounded-sm" onClick={() => navigate("/anunciar")} data-testid="plan-anuncio-btn">Anunciar grátis</Button>
          </div>

          {/* CARTAZ */}
          <div className="bg-white border border-border rounded-sm p-8" data-testid="plan-cartaz">
            <div className="w-12 h-12 rounded-sm bg-blaze flex items-center justify-center"><QrCode className="w-6 h-6 text-white" /></div>
            <h3 className="mt-5 font-heading font-800 text-2xl">Kit de Divulgação com QR</h3>
            <p className="mt-2 font-heading font-900 text-5xl text-obsidian">{formatBRLc(29)}</p>
            <p className="text-sm text-muted-foreground">assinatura trimestral • a cada 3 meses • opcional</p>
            <ul className="mt-6 space-y-3 text-sm">
              {["QR Code exclusivo do PicCar (leva ao seu anúncio)", "3 materiais prontos: Cartaz A4, Cartão Postal e Cartão de Visita", "Escolha cor e foto do carro nos materiais", "QR ativo enquanto a assinatura estiver ativa", "Cancele quando quiser — sem reimprimir nada"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button variant="outline" className="mt-8 w-full rounded-sm border-obsidian/15 font-700" onClick={() => navigate("/painel")} data-testid="plan-cartaz-btn">Gerar no meu anúncio</Button>
            <button onClick={() => navigate("/materiais")} className="mt-3 w-full text-sm font-700 text-blaze hover:underline" data-testid="plan-cartaz-exemplos">Ver exemplos dos materiais →</button>
          </div>

          {/* DESTAQUE */}
          <div className="bg-white border border-border rounded-sm p-8" data-testid="plan-destaque">
            <div className="w-12 h-12 rounded-sm bg-blaze flex items-center justify-center"><Rocket className="w-6 h-6 text-white" /></div>
            <h3 className="mt-5 font-heading font-800 text-2xl">Destaque no Topo</h3>
            <p className="mt-2 text-sm text-muted-foreground">assinatura • renova até cancelar • opcional</p>
            <div className="mt-4 space-y-2">
              {DESTAQUE.map((d) => (
                <div key={d.label} className="flex items-center justify-between border border-border rounded-sm px-3 py-2" data-testid={`precos-destaque-${d.label.split(" ")[0]}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-700">{d.label}</span>
                    {d.tag && <span className="bg-blaze/10 text-blaze text-[10px] font-800 uppercase px-1.5 py-0.5 rounded-sm">{d.tag}</span>}
                  </div>
                  <div className="text-right">
                    <span className="font-800 text-blaze">{formatBRLc(d.price)}</span>
                    <span className="block text-[11px] text-muted-foreground">{d.every}</span>
                  </div>
                </div>
              ))}
            </div>
            <ul className="mt-5 space-y-3 text-sm">
              {["Seu carro no topo da vitrine", "Selo Destaque no anúncio", "Cancele quando quiser"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button variant="outline" className="mt-6 w-full rounded-sm border-obsidian/15 font-700" onClick={() => navigate("/painel")} data-testid="plan-destaque-btn">Destacar meu anúncio</Button>
          </div>
        </div>

        {/* reassurance strip */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: InfinityIcon, t: "Anúncio ilimitado", d: "Quantos carros quiser, sem pagar nada." },
            { icon: ShieldCheck, t: "Sem comissão", d: "Vendeu? O dinheiro é 100% seu." },
            { icon: Printer, t: "Pague só o opcional", d: "Cartaz e destaque só se você quiser." },
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-3 bg-secondary/50 rounded-sm p-4">
              <r.icon className="w-6 h-6 text-blaze shrink-0" strokeWidth={1.5} />
              <div><p className="font-700 text-sm">{r.t}</p><p className="text-xs text-muted-foreground">{r.d}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-24" data-testid="faq-section">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-11 h-11 rounded-sm bg-obsidian flex items-center justify-center shrink-0">
            <HelpCircle className="w-6 h-6 text-blaze" />
          </span>
          <div>
            <p className="overline text-blaze mb-1">Dúvidas frequentes</p>
            <h2 className="font-heading font-800 text-2xl md:text-3xl">Perguntas sobre preços</h2>
          </div>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-border" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-700 hover:text-blaze hover:no-underline" data-testid={`faq-trigger-${i}`}>
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-10 bg-obsidian rounded-sm p-8 text-center text-white">
          <h3 className="font-heading font-800 text-2xl">Ainda com dúvida? É simples: anuncie de graça.</h3>
          <Button className="mt-5 h-12 px-8 bg-blaze hover:bg-white hover:text-obsidian text-white font-700 rounded-sm" onClick={() => navigate("/anunciar")} data-testid="faq-cta-btn">
            Anunciar grátis agora <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
